/**
 * Geographic data endpoints — AIMS backend.
 *
 *   GET /api/v1/aims/admin0/                    → Admin0Option[]  (country with iso3)
 *   GET /api/v1/aims/admin1/?adm0_code=…        → AdminOption[]   (province under a country)
 *   GET /api/v1/aims/admin2/?adm1_code=…        → AdminOption[]   (district under a province)
 *   GET /api/v1/aims/admin3/?adm2_code=…        → AdminOption[]   (community under a district)
 *
 *   GET /api/v1/aims/countries/                 → CountryOption[]
 *   GET /api/v1/aims/regional-bureaus/          → RegionalBureauOption[]
 *
 * Cascading dropdown pattern:
 *   country (admin0) → admin1?adm0_code=:iso3 → admin2?adm1_code=:id → admin3?adm2_code=:id
 *
 * The exact codes the backend expects in the query params (numeric id vs.
 * iso3 vs. some other code) are confirmed by inspecting actual responses
 * — pages should pass through whatever `value` the parent endpoint
 * returned for the selected option.
 */

import { aimsBackendApi } from "./api"
import { Admin0Option, AdminOption, CountryOption, RegionalBureauOption } from "./types"

export const aimsGeoApi = aimsBackendApi.injectEndpoints({
  endpoints: builder => ({
    listAdmin0: builder.query<Admin0Option[], void>({
      query: () => "api/v1/aims/admin0/",
      providesTags: [{ type: "AimsAdmin" as const, id: "ADMIN0" }],
    }),
    listAdmin1: builder.query<AdminOption[], { adm0_code?: number } | void>({
      query: params => ({ url: "api/v1/aims/admin1/", params: params || undefined }),
      providesTags: (_r, _e, arg) => {
        const code = arg?.adm0_code ?? "ALL"
        return [{ type: "AimsAdmin" as const, id: `ADMIN1:${code}` }]
      },
    }),
    listAdmin2: builder.query<AdminOption[], { adm1_code: number } | void>({
      query: params => ({ url: "api/v1/aims/admin2/", params: params || undefined }),
      providesTags: (_r, _e, arg) => [
        { type: "AimsAdmin" as const, id: `ADMIN2:${arg ? (arg as any).adm1_code : "ALL"}` },
      ],
    }),
    listAdmin3: builder.query<AdminOption[], { adm2_code: number } | void>({
      query: params => ({ url: "api/v1/aims/admin3/", params: params || undefined }),
      providesTags: (_r, _e, arg) => [
        { type: "AimsAdmin" as const, id: `ADMIN3:${arg ? (arg as any).adm2_code : "ALL"}` },
      ],
    }),

    listCountries: builder.query<CountryOption[], void>({
      query: () => "api/v1/aims/countries/",
      providesTags: [{ type: "AimsCountry" as const, id: "LIST" }],
    }),

    listRegionalBureaus: builder.query<RegionalBureauOption[], void>({
      query: () => "api/v1/aims/regional-bureaus/",
      providesTags: [{ type: "AimsRegionalBureau" as const, id: "LIST" }],
    }),
  }),
})

export const {
  useListAdmin0Query,
  useListAdmin1Query,
  useListAdmin2Query,
  useListAdmin3Query,
  useListCountriesQuery,
  useListRegionalBureausQuery,
} = aimsGeoApi
